<script setup lang="ts">
import { Download as DownloadIcon, RotateCcw as ResetIcon } from '@lucide/vue'
import { useDebounceFn } from '@vueuse/core'
import maplibregl, { type GeoJSONSource, type Map as MapLibreMap } from 'maplibre-gl'
import { computed, inject, onBeforeUnmount, ref, shallowRef, watch, type Ref } from 'vue'
import { onBeforeRouteLeave, useRouter } from 'vue-router'

import MapToolSheet from '@/components/map/MapToolSheet.vue'
import BaseButton from '@/components/ui/BaseButton.vue'
import BaseCheckbox from '@/components/ui/BaseCheckbox.vue'
import BaseToolbox from '@/components/ui/BaseToolbox.vue'
import { DEFAULT_MAP_STYLE_PREFERENCE } from '@/config/mapStyle'
import { connectivityService } from '@/services/connectivityService'
import { showConfirm } from '@/services/dialogService'
import { formatByteSize } from '@/services/formattingService'
import { logService, LOG_CATEGORIES } from '@/services/logService'
import {
  assertManualMapEstimateWithinLimits,
  coverageCellBounds,
  coverageCellContains,
  MANUAL_MAP_PACKAGE_ID,
  normalizeCoverageCells,
  selectionZoomForMapZoom,
  toggleCoverageCell,
  type OfflineCoverageCell,
  type OfflineEstimate,
  type TileSourceInfo,
} from '@/services/offlineTilesService'
import { isTauri } from '@/services/platformService'
import { isSmallScreen } from '@/services/uiService'
import { useOfflineStore } from '@/stores/offlineStore'

defineOptions({ name: 'OfflineMapAreaSelector' })

const SOURCE_ID = 'offline-map-area-selector-source'
const FILL_LAYER_ID = 'offline-map-area-selector-fill'
const LINE_LAYER_ID = 'offline-map-area-selector-line'
const MAX_VISIBLE_GRID_CELLS = 4096

type GridCellState = 'empty' | 'stored' | 'added' | 'removed' | 'partial'
type GridFeatureProperties = OfflineCoverageCell & { state: GridCellState }

const router = useRouter()
const offlineStore = useOfflineStore()
const injectedMap = inject<Ref<MapLibreMap | null>>('mapInstance', ref(null))
const mapLoaded = inject<Ref<boolean>>('mapLoaded', ref(false))
const downloadStylePreference = { ...DEFAULT_MAP_STYLE_PREFERENCE }
const selectorOwnerUserId = offlineStore.ownerUserId

const storedCoverage = ref<OfflineCoverageCell[]>(
  normalizeCoverageCells(offlineStore.manualMapEntry?.coverageCells ?? []),
)
const draftCoverage = ref<OfflineCoverageCell[]>([...storedCoverage.value])
const initialIncludeTerrain =
  offlineStore.manualMapEntry?.includeTerrain ?? offlineStore.getSavedIncludeTerrain()
const includeTerrain = ref(initialIncludeTerrain)
const sources = ref<TileSourceInfo[]>([])
const estimate = ref<OfflineEstimate | null>(null)
const estimateError = ref<string | null>(null)
const preparationError = ref<string | null>(null)
const selectionError = ref<string | null>(null)
const isPreparing = ref(true)
const isApplying = ref(false)
// shallowRef: a MapLibre Map is a large class instance we never want deeply reactive, and deep
// unwrapping mangles its type (drops private members), breaking assignability at call sites.
const boundMap = shallowRef<MapLibreMap | null>(null)

let previousCamera: {
  center: [number, number]
  zoom: number
  pitch: number
  bearing: number
} | null = null
let hasFittedStoredCoverage = false
let allowRouteLeave = false

const terrainAvailable = computed(() =>
  sources.value.some((source) => source.category === 'terrain'),
)
const selectedTileCount = computed(() => {
  if (!estimate.value) return 0
  return (
    estimate.value.mapData.tileCount + (includeTerrain.value ? estimate.value.terrain.tileCount : 0)
  )
})
const selectedEstimateBytes = computed(() => {
  if (!estimate.value) return 0
  return (
    estimate.value.mapData.estimatedBytes +
    (includeTerrain.value ? estimate.value.terrain.estimatedBytes : 0)
  )
})
const blockingError = computed(
  () => preparationError.value ?? selectionError.value ?? estimateError.value,
)
const normalizedDraftKey = computed(() =>
  JSON.stringify(normalizeCoverageCells(draftCoverage.value)),
)
const storedCoverageKey = computed(() => JSON.stringify(storedCoverage.value))
const hasChanges = computed(
  () =>
    normalizedDraftKey.value !== storedCoverageKey.value ||
    (draftCoverage.value.length > 0 &&
      includeTerrain.value !== (offlineStore.manualMapEntry?.includeTerrain ?? false)),
)
const actionLabel = computed(() => {
  if (draftCoverage.value.length === 0 && storedCoverage.value.length > 0) return 'Remove all'
  return storedCoverage.value.length > 0 ? 'Apply changes' : 'Download'
})

function longitudeToTileX(lon: number, zoom: number): number {
  return Math.floor(((lon + 180) / 360) * 2 ** zoom)
}

function latitudeToTileY(lat: number, zoom: number): number {
  const clamped = Math.max(-85.05112878, Math.min(85.05112878, lat))
  const radians = (clamped * Math.PI) / 180
  return Math.floor(
    ((1 - Math.log(Math.tan(radians) + 1 / Math.cos(radians)) / Math.PI) / 2) * 2 ** zoom,
  )
}

function gridCellState(cell: OfflineCoverageCell): GridCellState {
  const coverageState = (coverage: OfflineCoverageCell[]): 'none' | 'partial' | 'full' => {
    if (coverage.some((candidate) => coverageCellContains(candidate, cell))) return 'full'
    if (coverage.some((candidate) => coverageCellContains(cell, candidate))) return 'partial'
    return 'none'
  }
  const stored = coverageState(storedCoverage.value)
  const selected = coverageState(draftCoverage.value)
  if (selected === 'full' && stored !== 'full') return 'added'
  if (stored !== 'none' && selected === 'none') return 'removed'
  if (selected === 'full' && stored === 'full') return 'stored'
  if (stored === 'partial' || selected === 'partial') return 'partial'
  return 'empty'
}

function buildVisibleGrid(
  map: MapLibreMap,
): GeoJSON.FeatureCollection<GeoJSON.Polygon, GridFeatureProperties> {
  const zoom = selectionZoomForMapZoom(map.getZoom())
  const tileScale = 2 ** zoom
  const bounds = map.getBounds()
  const minX = longitudeToTileX(bounds.getWest(), zoom)
  const maxX = longitudeToTileX(bounds.getEast(), zoom)
  const minY = Math.max(0, latitudeToTileY(bounds.getNorth(), zoom))
  const maxY = Math.min(tileScale - 1, latitudeToTileY(bounds.getSouth(), zoom))
  const features: Array<GeoJSON.Feature<GeoJSON.Polygon, GridFeatureProperties>> = []
  const seen = new Set<string>()

  for (let unwrappedX = minX; unwrappedX <= maxX; unwrappedX++) {
    const x = ((unwrappedX % tileScale) + tileScale) % tileScale
    for (let y = minY; y <= maxY; y++) {
      const key = `${zoom}/${x}/${y}`
      if (seen.has(key)) continue
      seen.add(key)
      const cell = { z: zoom, x, y }
      const cellBounds = coverageCellBounds(cell)
      features.push({
        type: 'Feature',
        properties: { ...cell, state: gridCellState(cell) },
        geometry: {
          type: 'Polygon',
          coordinates: [
            [
              [cellBounds.minLon, cellBounds.minLat],
              [cellBounds.maxLon, cellBounds.minLat],
              [cellBounds.maxLon, cellBounds.maxLat],
              [cellBounds.minLon, cellBounds.maxLat],
              [cellBounds.minLon, cellBounds.minLat],
            ],
          ],
        },
      })
      if (features.length >= MAX_VISIBLE_GRID_CELLS) {
        return { type: 'FeatureCollection', features }
      }
    }
  }

  return { type: 'FeatureCollection', features }
}

function refreshGrid(): void {
  const map = boundMap.value
  if (!map?.isStyleLoaded()) return
  const data = buildVisibleGrid(map)
  const source = map.getSource(SOURCE_ID) as GeoJSONSource | undefined
  if (source) {
    source.setData(data)
    return
  }

  map.addSource(SOURCE_ID, { type: 'geojson', data })
  map.addLayer({
    id: FILL_LAYER_ID,
    type: 'fill',
    source: SOURCE_ID,
    paint: {
      'fill-color': [
        'match',
        ['get', 'state'],
        'stored',
        '#047857',
        'added',
        '#10b981',
        'removed',
        '#dc2626',
        'partial',
        '#d97706',
        '#ffffff',
      ],
      'fill-opacity': [
        'match',
        ['get', 'state'],
        'stored',
        0.24,
        'added',
        0.36,
        'removed',
        0.3,
        'partial',
        0.2,
        0.04,
      ],
    },
  })
  map.addLayer({
    id: LINE_LAYER_ID,
    type: 'line',
    source: SOURCE_ID,
    paint: {
      'line-color': [
        'match',
        ['get', 'state'],
        'added',
        '#047857',
        'stored',
        '#065f46',
        'removed',
        '#b91c1c',
        'partial',
        '#b45309',
        '#475569',
      ],
      'line-opacity': 0.75,
      'line-width': ['match', ['get', 'state'], 'empty', 1, 2],
    },
  })
}

function handleGridClick(event: maplibregl.MapLayerMouseEvent): void {
  const properties = event.features?.[0]?.properties as Partial<GridFeatureProperties> | undefined
  if (
    !properties ||
    typeof properties.z !== 'number' ||
    typeof properties.x !== 'number' ||
    typeof properties.y !== 'number'
  ) {
    return
  }
  try {
    draftCoverage.value = toggleCoverageCell(draftCoverage.value, {
      z: properties.z,
      x: properties.x,
      y: properties.y,
    })
    selectionError.value = null
    refreshGrid()
  } catch (error) {
    selectionError.value = error instanceof Error ? error.message : 'Could not change this area'
  }
}

function handleMouseEnter(): void {
  boundMap.value?.getCanvas().style.setProperty('cursor', 'pointer')
}

function handleMouseLeave(): void {
  boundMap.value?.getCanvas().style.removeProperty('cursor')
}

function detachMap(): void {
  const map = boundMap.value
  if (!map) return
  map.off('moveend', refreshGrid)
  map.off('styledata', refreshGrid)
  map.off('click', FILL_LAYER_ID, handleGridClick)
  map.off('mouseenter', FILL_LAYER_ID, handleMouseEnter)
  map.off('mouseleave', FILL_LAYER_ID, handleMouseLeave)
  if (map.getLayer(LINE_LAYER_ID)) map.removeLayer(LINE_LAYER_ID)
  if (map.getLayer(FILL_LAYER_ID)) map.removeLayer(FILL_LAYER_ID)
  if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID)
  map.getCanvas().style.removeProperty('cursor')
  boundMap.value = null
}

function attachMap(map: MapLibreMap): void {
  if (boundMap.value === map) {
    refreshGrid()
    return
  }
  detachMap()
  boundMap.value = map
  const center = map.getCenter()
  previousCamera = {
    center: [center.lng, center.lat],
    zoom: map.getZoom(),
    pitch: map.getPitch(),
    bearing: map.getBearing(),
  }
  map.jumpTo({ pitch: 0, bearing: 0 })
  map.on('moveend', refreshGrid)
  map.on('styledata', refreshGrid)
  refreshGrid()
  map.on('click', FILL_LAYER_ID, handleGridClick)
  map.on('mouseenter', FILL_LAYER_ID, handleMouseEnter)
  map.on('mouseleave', FILL_LAYER_ID, handleMouseLeave)

  if (!hasFittedStoredCoverage && storedCoverage.value.length > 0) {
    hasFittedStoredCoverage = true
    const coverageBounds = storedCoverage.value.map(coverageCellBounds)
    const west = Math.min(...coverageBounds.map((bounds) => bounds.minLon))
    const east = Math.max(...coverageBounds.map((bounds) => bounds.maxLon))
    const south = Math.min(...coverageBounds.map((bounds) => bounds.minLat))
    const north = Math.max(...coverageBounds.map((bounds) => bounds.maxLat))
    if (east - west <= 180) {
      map.fitBounds(
        [
          [west, south],
          [east, north],
        ],
        { padding: { top: 80, right: 40, bottom: isSmallScreen.value ? 260 : 220, left: 40 } },
      )
    }
  }
}

function calculateEstimate(): OfflineEstimate | null {
  if (
    !sources.value.some((source) => source.category === 'mapData' && source.sourceId === 'base')
  ) {
    estimate.value = null
    return null
  }
  estimateError.value = null
  try {
    const nextEstimate = offlineStore.estimateManualMapSize(draftCoverage.value, sources.value)
    assertManualMapEstimateWithinLimits({
      ...nextEstimate,
      tileCount:
        nextEstimate.mapData.tileCount +
        (includeTerrain.value ? nextEstimate.terrain.tileCount : 0),
      estimatedBytes:
        nextEstimate.mapData.estimatedBytes +
        (includeTerrain.value ? nextEstimate.terrain.estimatedBytes : 0),
    })
    estimate.value = nextEstimate
    return nextEstimate
  } catch (error) {
    estimate.value = null
    estimateError.value = error instanceof Error ? error.message : 'Could not estimate this area'
    return null
  }
}

const recalculateEstimate = useDebounceFn(calculateEstimate, 120)

async function prepareSelector(): Promise<void> {
  isPreparing.value = true
  preparationError.value = null
  try {
    sources.value = await offlineStore.discoverManualMapSources(downloadStylePreference)
    if (!terrainAvailable.value) includeTerrain.value = false
    recalculateEstimate()
  } catch (error) {
    preparationError.value =
      error instanceof Error ? error.message : 'Could not prepare downloadable map data'
    logService.error(LOG_CATEGORIES.OFFLINE, 'Failed to prepare manual map selector', { error })
  } finally {
    isPreparing.value = false
  }
}

function resetChanges(): void {
  draftCoverage.value = [...storedCoverage.value]
  includeTerrain.value = initialIncludeTerrain
  selectionError.value = null
  estimateError.value = null
  refreshGrid()
}

async function closeSelector(): Promise<void> {
  await router.replace({ name: 'offline-data' })
}

async function applyChanges(): Promise<void> {
  if (
    !hasChanges.value ||
    preparationError.value ||
    selectionError.value ||
    isApplying.value ||
    !connectivityService.isEffectivelyOnline.value
  ) {
    return
  }

  if (!calculateEstimate()) return

  if (draftCoverage.value.length === 0) {
    const confirmed = await showConfirm({
      title: 'Remove downloaded map areas?',
      message: 'Delete all manually downloaded map tiles and contours from this device?',
      confirmText: 'Remove',
      cancelText: 'Cancel',
      variant: 'danger',
    })
    if (!confirmed) return
  }

  if (
    draftCoverage.value.length > 0 &&
    selectedEstimateBytes.value > offlineStore.LARGE_DOWNLOAD_BYTES_THRESHOLD
  ) {
    const confirmed = await showConfirm({
      title: 'Download a large map area?',
      message: `The estimated total size is ${formatByteSize(selectedEstimateBytes.value)}. Continue only if the device has enough free storage.`,
      confirmText: 'Download',
      cancelText: 'Cancel',
      variant: 'warning',
    })
    if (!confirmed) return
  }

  isApplying.value = true
  try {
    if (draftCoverage.value.length === 0) {
      await offlineStore.deleteOffline(MANUAL_MAP_PACKAGE_ID)
    } else {
      await offlineStore.makeManualMapOffline(
        draftCoverage.value,
        downloadStylePreference,
        includeTerrain.value,
        sources.value,
      )
    }
    allowRouteLeave = true
    await closeSelector()
  } catch (error) {
    estimateError.value = error instanceof Error ? error.message : 'Could not update map areas'
    logService.error(LOG_CATEGORIES.OFFLINE, 'Failed to apply manual map selection', { error })
  } finally {
    isApplying.value = false
  }
}

watch(
  [injectedMap, mapLoaded],
  ([map, loaded]) => {
    if (map && loaded) attachMap(map)
  },
  { immediate: true },
)
watch([normalizedDraftKey, includeTerrain], () => recalculateEstimate())
watch(
  () => [offlineStore.ownerUserId, connectivityService.isEffectivelyOnline.value] as const,
  ([ownerUserId, online]) => {
    if (ownerUserId === selectorOwnerUserId && ownerUserId !== null && online && isTauri()) return
    allowRouteLeave = true
    void router.replace({ name: 'offline-data' })
  },
  { immediate: true },
)

onBeforeRouteLeave(async () => {
  if (allowRouteLeave || !hasChanges.value) return true
  return showConfirm({
    title: 'Discard map area changes?',
    message: 'Your selected and removed map areas have not been applied.',
    confirmText: 'Discard',
    cancelText: 'Keep editing',
    variant: 'danger',
  })
})

if (isTauri() && selectorOwnerUserId !== null && connectivityService.isEffectivelyOnline.value) {
  void prepareSelector()
}

onBeforeUnmount(() => {
  const map = boundMap.value
  detachMap()
  if (map && previousCamera) map.jumpTo(previousCamera)
})
</script>

<template>
  <div class="offline-map-area-selector pointer-events-none absolute inset-0 z-50">
    <div
      class="pointer-events-none absolute left-1/2 top-[calc(env(safe-area-inset-top)+0.75rem)] -translate-x-1/2 rounded-full border border-white/70 bg-gray-950/75 px-4 py-2 text-center text-xs font-medium text-white shadow-lg backdrop-blur sm:text-sm"
    >
      Tap grid cells to add or remove coverage. Zoom out to select larger areas.
    </div>

    <MapToolSheet
      v-if="isSmallScreen"
      :open="true"
      title="Select map areas"
      initial-mode="mid"
      testid="offline-map-area-sheet"
      @close="closeSelector"
    >
      <div class="space-y-4 px-4 pb-4 pt-2">
        <div class="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
          <span class="inline-flex items-center gap-1.5"
            ><span class="h-3 w-3 rounded-sm bg-emerald-700/50"></span>Downloaded</span
          >
          <span class="inline-flex items-center gap-1.5"
            ><span class="h-3 w-3 rounded-sm bg-emerald-500/60"></span>Adding</span
          >
          <span class="inline-flex items-center gap-1.5"
            ><span class="h-3 w-3 rounded-sm bg-red-600/50"></span>Removing</span
          >
          <span class="inline-flex items-center gap-1.5"
            ><span class="h-3 w-3 rounded-sm bg-amber-600/40"></span>Partial</span
          >
        </div>
        <div class="rounded-lg bg-gray-50 p-3 text-sm">
          <div class="flex items-center justify-between gap-4">
            <span class="text-gray-600">Estimated total size</span>
            <strong class="tabular-nums">{{ formatByteSize(selectedEstimateBytes) }}</strong>
          </div>
          <div class="mt-1 flex items-center justify-between gap-4 text-xs text-gray-500">
            <span>Routecamp map tiles (z0–14)</span
            ><span>{{ selectedTileCount.toLocaleString() }}</span>
          </div>
        </div>
        <label v-if="terrainAvailable" class="flex min-h-11 items-start gap-3">
          <BaseCheckbox v-model="includeTerrain" class="mt-0.5" />
          <span class="text-sm text-gray-700">Contours and terrain shading</span>
        </label>
        <p v-if="blockingError" class="text-sm text-red-700" role="alert">{{ blockingError }}</p>
        <div class="flex gap-2">
          <BaseButton
            variant="secondary"
            :disabled="!hasChanges || isApplying"
            @click="resetChanges"
          >
            <ResetIcon class="mr-1.5 h-4 w-4" />Reset
          </BaseButton>
          <BaseButton
            class="flex-1"
            :loading="isApplying"
            :disabled="
              !hasChanges ||
              isPreparing ||
              !!blockingError ||
              !connectivityService.isEffectivelyOnline.value
            "
            @click="applyChanges"
          >
            <DownloadIcon v-if="!isApplying" class="mr-1.5 h-4 w-4" />{{ actionLabel }}
          </BaseButton>
        </div>
      </div>
    </MapToolSheet>

    <BaseToolbox
      v-else
      :is-open="true"
      title="Select map areas"
      placement="bottom"
      bottom-align="center"
      :bottom-max-width="560"
      :draggable="false"
      :collapsible="false"
      panel-class="pointer-events-auto"
      body-class="p-4"
      @close="closeSelector"
    >
      <div class="space-y-4">
        <div class="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
          <span class="inline-flex items-center gap-1.5"
            ><span class="h-3 w-3 rounded-sm bg-emerald-700/50"></span>Downloaded</span
          >
          <span class="inline-flex items-center gap-1.5"
            ><span class="h-3 w-3 rounded-sm bg-emerald-500/60"></span>Adding</span
          >
          <span class="inline-flex items-center gap-1.5"
            ><span class="h-3 w-3 rounded-sm bg-red-600/50"></span>Removing</span
          >
          <span class="inline-flex items-center gap-1.5"
            ><span class="h-3 w-3 rounded-sm bg-amber-600/40"></span>Partial</span
          >
        </div>
        <div class="grid grid-cols-2 gap-3 rounded-lg bg-gray-50 p-3 text-sm">
          <div>
            <p class="text-xs text-gray-500">Estimated total size</p>
            <p class="font-semibold tabular-nums">{{ formatByteSize(selectedEstimateBytes) }}</p>
          </div>
          <div>
            <p class="text-xs text-gray-500">Routecamp tiles (z0–14)</p>
            <p class="font-semibold tabular-nums">{{ selectedTileCount.toLocaleString() }}</p>
          </div>
        </div>
        <label v-if="terrainAvailable" class="flex min-h-11 items-center gap-3">
          <BaseCheckbox v-model="includeTerrain" />
          <span class="text-sm text-gray-700">Contours and terrain shading</span>
        </label>
        <p v-if="blockingError" class="text-sm text-red-700" role="alert">{{ blockingError }}</p>
        <div class="flex justify-end gap-2">
          <BaseButton
            variant="secondary"
            :disabled="!hasChanges || isApplying"
            @click="resetChanges"
          >
            <ResetIcon class="mr-1.5 h-4 w-4" />Reset
          </BaseButton>
          <BaseButton
            :loading="isApplying"
            :disabled="
              !hasChanges ||
              isPreparing ||
              !!blockingError ||
              !connectivityService.isEffectivelyOnline.value
            "
            @click="applyChanges"
          >
            <DownloadIcon v-if="!isApplying" class="mr-1.5 h-4 w-4" />{{ actionLabel }}
          </BaseButton>
        </div>
      </div>
    </BaseToolbox>
  </div>
</template>
